import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/src/js/lib/utils";

// Preflight is off, so the browser's own border, padding and shadow stay on buttons unless reset
// inline, where no stylesheet rule can bring them back
const BARE_BUTTON_STYLE: React.CSSProperties = {
  appearance: "none",
  border: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
  padding: 0,
};

const NAV_BUTTON_CLASS =
  "tw:z-10 tw:inline-flex tw:size-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:text-[#17436d] tw:outline-none tw:hover:bg-neutral-100 tw:focus-visible:ring-[3px] tw:focus-visible:ring-neutral-950/50";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("tw:p-0 tw:sm:p-3", className)}
      styles={{
        button_previous: BARE_BUTTON_STYLE,
        button_next: BARE_BUTTON_STYLE,
        day_button: BARE_BUTTON_STYLE,
      }}
      classNames={{
        months: "tw:relative tw:flex tw:flex-wrap tw:gap-6",
        // Two months need about 580px, so below the lg breakpoint only the first shows. The arrows
        // still step one month at a time
        month: "tw:grid tw:gap-2 tw:max-lg:[&+&]:hidden",
        nav: "tw:absolute tw:inset-x-0 tw:top-0 tw:flex tw:items-center tw:justify-between",
        button_previous: NAV_BUTTON_CLASS,
        button_next: NAV_BUTTON_CLASS,
        month_caption: "tw:flex tw:h-8 tw:items-center tw:justify-center",
        // With month and year dropdowns, the native select sits invisibly over its label so the
        // label and chevron are what shows, and the wrapper takes the focus ring
        dropdowns: "tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:text-sm tw:font-medium",
        dropdown_root:
          "tw:relative tw:rounded-md tw:border tw:border-neutral-200 tw:has-focus:ring-[3px] tw:has-focus:ring-neutral-950/50",
        dropdown: "tw:absolute tw:inset-0 tw:cursor-pointer tw:opacity-0",
        caption_label:
          "tw:flex tw:h-8 tw:items-center tw:gap-1 tw:rounded-md tw:pr-1 tw:pl-2 tw:text-sm tw:font-medium",
        month_grid: "tw:border-collapse",
        weekdays: "tw:flex",
        weekday: "tw:w-9 tw:text-center tw:text-xs tw:font-normal tw:text-neutral-500",
        weeks: "tw:grid tw:gap-1",
        week: "tw:flex",
        // The cell holds the range's fill, so the days of a range join into one band
        day: "tw:size-9 tw:p-0 tw:text-center tw:text-sm tw:[&>button]:text-inherit",
        day_button:
          "tw:size-9 tw:cursor-pointer tw:rounded-md tw:text-sm tw:outline-none tw:hover:bg-neutral-100 tw:focus-visible:ring-[3px] tw:focus-visible:ring-neutral-950/50",
        range_start:
          "tw:rounded-l-md tw:bg-[#315b75] tw:text-white tw:[&>button]:hover:bg-[#315b75]",
        range_end: "tw:rounded-r-md tw:bg-[#315b75] tw:text-white tw:[&>button]:hover:bg-[#315b75]",
        range_middle: "tw:bg-[#b1cfe0] tw:text-neutral-900",
        today: "tw:font-bold",
        outside: "tw:text-neutral-400",
        disabled: "tw:text-neutral-300",
        hidden: "tw:invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === "left") return <ChevronLeftIcon className="tw:size-4" />;
          if (orientation === "down") return <ChevronDownIcon className="tw:size-3.5" />;
          return <ChevronRightIcon className="tw:size-4" />;
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
